import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Truck, AlertTriangle, Gavel, Check, X, Loader2, Users } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const CLAIM_COLOR = {
  assessing: "#2563EB", reassessing: "#2563EB", approved: "#16A34A", denied: "#697169",
};

function Shortfalls() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/admin/shipping-shortfalls").then((r) => setData(r.data)).catch(() => setError("Could not load report"));
  }, []);
  if (error) return <div className="hairline p-6 text-sm" data-testid="admin-error" style={{ color: "#B91C1C" }}>{error}</div>;
  if (!data) return <div className="label-eyebrow" data-testid="admin-loading">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <div className="hairline p-6" data-testid="admin-total">
          <div className="label-eyebrow mb-2 flex items-center gap-2"><AlertTriangle size={14} /> Total absorbed by platform</div>
          <div className="font-display text-4xl" style={{ color: data.total_shortfall > 0 ? "#B91C1C" : "#1E3A2B" }}>{money(data.total_shortfall)}</div>
        </div>
        <div className="hairline p-6" data-testid="admin-count">
          <div className="label-eyebrow mb-2 flex items-center gap-2"><Truck size={14} /> Orders with a shortfall</div>
          <div className="font-display text-4xl">{data.count}</div>
        </div>
      </div>
      {data.orders.length === 0 ? (
        <div className="hairline p-10 text-center text-sm text-neutral-500" data-testid="admin-empty">
          No shipping shortfalls — every label so far was covered by what buyers paid. 🎉
        </div>
      ) : (
        <div className="hairline overflow-x-auto" data-testid="admin-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-eyebrow border-b border-black/10">
                <th className="p-3">Item</th><th className="p-3">Seller</th><th className="p-3">Carrier</th>
                <th className="p-3 text-right">Label</th><th className="p-3 text-right">Buyer paid</th>
                <th className="p-3 text-right">Shortfall</th><th className="p-3">Shipped</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.order_id} className="border-b border-black/5" data-testid={`shortfall-row-${o.order_id}`}>
                  <td className="p-3">{o.title || "—"}</td>
                  <td className="p-3 text-neutral-600">{o.seller_name || "—"}</td>
                  <td className="p-3 text-neutral-600">{o.carrier} {o.service}</td>
                  <td className="p-3 text-right">{money(o.label_cost)}</td>
                  <td className="p-3 text-right">{money(o.buyer_paid)}</td>
                  <td className="p-3 text-right font-display" style={{ color: "#B91C1C" }}>{money(o.shortfall)}</td>
                  <td className="p-3 text-neutral-600">{fmtDate(o.shipped_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Orders() {
  const [data, setData] = useState(null);
  const [disputesOnly, setDisputesOnly] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null); // { oid, decision, title }

  const load = () => api.get(`/admin/orders${disputesOnly ? "?disputes=true" : ""}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { setData(null); load(); }, [disputesOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const doResolve = async () => {
    if (!confirm) return;
    const { oid, decision } = confirm;
    setConfirm(null);
    setBusy(oid);
    try {
      await api.post(`/admin/orders/${oid}/resolve-claim`, { decision });
      toast.success(decision === "refund" ? "Buyer refunded — seller struck" : "Claim denied — payout released to seller");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="label-eyebrow" data-testid="admin-orders-loading">Loading…</div>;

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="hairline px-4 py-2 text-sm" data-testid="admin-orders-count"><strong>{data.count}</strong> orders</div>
        <div className="hairline px-4 py-2 text-sm" data-testid="admin-disputes-count">
          <strong style={{ color: data.open_disputes ? "#2563EB" : undefined }}>{data.open_disputes}</strong> open dispute{data.open_disputes === 1 ? "" : "s"} · {data.disputes} total
        </div>
        <label className="ml-auto flex items-center gap-2 text-sm cursor-pointer" data-testid="admin-disputes-filter">
          <input type="checkbox" checked={disputesOnly} onChange={(e) => setDisputesOnly(e.target.checked)} />
          Disputes only
        </label>
      </div>

      {data.orders.length === 0 ? (
        <div className="hairline p-10 text-center text-sm text-neutral-500" data-testid="admin-orders-empty">
          {disputesOnly ? "No disputes — everything's running smoothly." : "No orders yet."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-orders-list">
          {data.orders.map((o) => (
            <div key={o.order_id} className="hairline p-4 bg-white" data-testid={`admin-order-${o.order_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display">{o.title || "Item"}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {o.seller_name || "seller"} · {fmtDate(o.paid_at)} · {money(o.amount)} · net {money(o.seller_net)}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {o.fulfilment_status} · payout {o.payout_status}{o.tracking_number ? ` · ${o.carrier} ${o.tracking_number}` : ""}
                  </div>
                </div>
                {o.claim && (
                  <span className="chip" style={{ color: CLAIM_COLOR[o.claim.status] || "#697169" }} data-testid={`admin-claim-status-${o.order_id}`}>
                    Claim: {o.claim.status}
                  </span>
                )}
              </div>

              {o.claim && (
                <div className="mt-3 hairline p-3" style={{ background: "rgba(37,99,235,0.04)" }}>
                  <div className="flex items-center gap-2 label-eyebrow" style={{ color: "#2563EB" }}>
                    <Gavel size={13} /> Dispute — {o.claim.reason}
                  </div>
                  {o.claim.note && <p className="mt-1.5 text-sm text-neutral-700">“{o.claim.note}”</p>}
                  {o.claim.ai?.reasoning && <p className="mt-1.5 text-xs text-neutral-500">AI: {o.claim.ai.reasoning}</p>}
                  {["assessing", "reassessing"].includes(o.claim.status) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button className="btn-primary text-[0.62rem] inline-flex items-center gap-1.5"
                        style={{ background: "#B91C1C" }} disabled={busy === o.order_id}
                        onClick={() => setConfirm({ oid: o.order_id, decision: "refund", title: o.title })} data-testid={`resolve-refund-${o.order_id}`}>
                        {busy === o.order_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Refund buyer
                      </button>
                      <button className="btn-outline text-[0.62rem] inline-flex items-center gap-1.5"
                        disabled={busy === o.order_id}
                        onClick={() => setConfirm({ oid: o.order_id, decision: "deny", title: o.title })} data-testid={`resolve-deny-${o.order_id}`}>
                        <X size={12} /> Side with seller
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="resolve-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.decision === "refund" ? "Refund the buyer?" : "Side with the seller?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.decision === "refund"
                ? `This will refund the buyer for “${confirm?.title}”, pull the listing, and apply a strike to the seller.`
                : `This will deny the claim on “${confirm?.title}” and release the payout to the seller.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="resolve-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doResolve} data-testid="resolve-confirm"
              style={confirm?.decision === "refund" ? { background: "#B91C1C" } : {}}>
              {confirm?.decision === "refund" ? "Refund buyer" : "Deny claim"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Members() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/admin/members").then((r) => setData(r.data)).catch(() => setError("Could not load members"));
  }, []);
  if (error) return <p className="text-sm text-neutral-500" data-testid="admin-members-error">{error}</p>;
  if (!data) return <p className="text-sm text-neutral-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>;
  return (
    <div data-testid="members-panel">
      <div className="label-eyebrow mb-3 flex items-center gap-2"><Users size={14} /> Members</div>
      <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
        <div className="hairline p-6" data-testid="members-total-card">
          <div className="font-display text-5xl tracking-tight" data-testid="members-total" style={{ color: "#1E3A2B" }}>{data.total}</div>
          <div className="mt-2 text-sm text-neutral-500">Total members</div>
        </div>
        <div className="hairline p-6" data-testid="members-sellers-card">
          <div className="font-display text-5xl tracking-tight">{data.sellers}</div>
          <div className="mt-2 text-sm text-neutral-500">Have listed an item</div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("shortfalls");
  const [memberCount, setMemberCount] = useState(null);
  useEffect(() => { api.get("/admin/members").then((r) => setMemberCount(r.data.total)).catch(() => {}); }, []);
  return (
    <div className="max-w-5xl mx-auto px-5 py-12" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck size={22} strokeWidth={1.5} style={{ color: "#1E3A2B" }} />
        <span className="label-eyebrow">Admin</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl mb-6">Control room</h1>

      <div className="flex gap-2 mb-8 border-b border-black/10">
        {[["shortfalls", "Shipping shortfalls"], ["orders", "Orders & disputes"], ["members", "Members"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className="px-4 py-2 text-sm -mb-px border-b-2 transition-colors inline-flex items-center gap-2"
            style={tab === k ? { borderColor: "#1E3A2B", color: "#1E3A2B", fontWeight: 600 } : { borderColor: "transparent", color: "#8a857b" }}>
            {label}
            {k === "members" && memberCount !== null && (
              <span className="text-[0.7rem] px-1.5 py-0.5 rounded-full" data-testid="admin-members-badge"
                style={{ background: "#1E3A2B", color: "#F9F6F0" }}>{memberCount}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "shortfalls" ? <Shortfalls /> : tab === "orders" ? <Orders /> : <Members />}
    </div>
  );
}
